import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function ContractTemplates() {
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [dealType, setDealType] = useState("");

  const { data: user } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: organization } = useQuery({
    queryKey: ["user-organization", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("organization_members")
        .select("organization_id, organizations(*)")
        .eq("user_id", user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: templates, isLoading } = useQuery({
    queryKey: ["contract-templates", organization?.organization_id],
    queryFn: async () => {
      if (!organization?.organization_id) return [];
      const { data, error } = await supabase
        .from("contract_templates")
        .select("*")
        .eq("organization_id", organization.organization_id)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!organization?.organization_id,
  });

  const createTemplateMutation = useMutation({
    mutationFn: async () => {
      if (!organization?.organization_id || !user?.id) throw new Error("Missing organization or user");
      
      const { error } = await supabase
        .from("contract_templates")
        .insert({
          organization_id: organization.organization_id,
          template_name: templateName,
          template_content: templateContent,
          deal_type: dealType || null,
          created_by: user.id,
          variables: extractVariables(templateContent),
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
      toast.success("Template created successfully");
      setIsCreateOpen(false);
      resetForm();
    },
    onError: () => {
      toast.error("Failed to create template");
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("contract_templates")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
      toast.success("Template deleted");
    },
    onError: () => {
      toast.error("Failed to delete template");
    },
  });

  const duplicateTemplateMutation = useMutation({
    mutationFn: async (template: any) => {
      if (!organization?.organization_id || !user?.id) throw new Error("Missing organization or user");
      
      const { error } = await supabase
        .from("contract_templates")
        .insert({
          organization_id: organization.organization_id,
          template_name: `${template.template_name} (Copy)`,
          template_content: template.template_content,
          deal_type: template.deal_type,
          created_by: user.id,
          variables: template.variables,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
      toast.success("Template duplicated");
    },
    onError: () => {
      toast.error("Failed to duplicate template");
    },
  });

  const extractVariables = (content: string): string[] => {
    const matches = content.match(/\{\{([^}]+)\}\}/g) || [];
    return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, "").trim()))];
  };

  const resetForm = () => {
    setTemplateName("");
    setTemplateContent("");
    setDealType("");
  };

  const sampleTemplate = `ATHLETE ENDORSEMENT AGREEMENT

This Agreement is entered into on {{signature_date}} between {{brand_name}} ("Brand") and {{athlete_name}} ("Athlete").

1. COMPENSATION
Brand agrees to pay Athlete a total of {{deal_amount}} for the services described herein.

2. TERM
This Agreement shall commence on {{start_date}} and terminate on {{end_date}}.

3. DELIVERABLES
Athlete agrees to provide the following:
{{deliverables}}

4. PAYMENT SCHEDULE
{{payment_schedule}}

5. ATHLETE INFORMATION
Name: {{athlete_name}}
Email: {{athlete_email}}
Sport: {{athlete_sport}}

By signing below, both parties agree to the terms of this Agreement.

_______________________          _______________________
{{brand_name}}                   {{athlete_name}}
Date: {{signature_date}}         Date: {{signature_date}}`;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Contract Templates</h1>
            <p className="text-muted-foreground">
              Create reusable contract templates for bulk generation
            </p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Contract Template</DialogTitle>
                <DialogDescription>
                  Use variables like {`{{athlete_name}}`}, {`{{deal_amount}}`}, {`{{start_date}}`} for dynamic content
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Template Name *</Label>
                  <Input
                    placeholder="e.g., Social Media Endorsement Agreement"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Deal Type (Optional)</Label>
                  <Select value={dealType} onValueChange={setDealType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select deal type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="appearance">Appearance</SelectItem>
                      <SelectItem value="endorsement">Endorsement</SelectItem>
                      <SelectItem value="merchandise">Merchandise</SelectItem>
                      <SelectItem value="autograph">Autograph Session</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Template Content *</Label>
                  <Textarea
                    placeholder="Enter your contract template..."
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    rows={20}
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setTemplateContent(sampleTemplate)}
                  >
                    Load Sample Template
                  </Button>
                </div>

                {templateContent && (
                  <div className="space-y-2">
                    <Label>Detected Variables</Label>
                    <div className="flex flex-wrap gap-2">
                      {extractVariables(templateContent).map((variable) => (
                        <Badge key={variable} variant="secondary">
                          {variable}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => createTemplateMutation.mutate()}
                  disabled={!templateName || !templateContent || createTemplateMutation.isPending}
                >
                  Create Template
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div>Loading templates...</div>
        ) : templates?.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No templates yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first contract template to streamline deal creation
              </p>
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {templates?.map((template) => (
              <Card key={template.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{template.template_name}</CardTitle>
                      <CardDescription>
                        {template.deal_type && (
                          <Badge variant="outline" className="mt-2">
                            {template.deal_type.replace("_", " ")}
                          </Badge>
                        )}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => duplicateTemplateMutation.mutate(template)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => deleteTemplateMutation.mutate(template.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Variables:</p>
                      <div className="flex flex-wrap gap-2">
                        {(template.variables as string[])?.map((variable) => (
                          <Badge key={variable} variant="secondary" className="text-xs">
                            {variable}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Preview:</p>
                      <div className="bg-muted p-3 rounded-md max-h-32 overflow-y-auto">
                        <pre className="text-xs whitespace-pre-wrap">
                          {template.template_content.substring(0, 200)}...
                        </pre>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
